//src/pages/PainelCampanhas.js

import React, { useEffect, useMemo, useState } from "react";
import axios, { BASE_URL } from "../axiosConfig";
import { Link } from "react-router-dom";
import ComProtecao from "../components/ComProtecao";
import {
  Edit,
  Eye,
  ImagePlus,
  Plus,
  Power,
  Search,
  Trash2,
  Users,
  X,
} from "lucide-react";
import "./PainelCampanhas.css";

function getImagemUrl(img) {
  if (!img) return "";
  if (img.startsWith("http://localhost:3001")) {
    return img.replace("http://localhost:3001", BASE_URL);
  }
  if (/^http:\/\/localhost:\d+/.test(img)) {
    return img.replace(/^http:\/\/localhost:\d+/, BASE_URL);
  }
  return img.startsWith("http") ? img : `${BASE_URL}${img}`;
}

function PainelCampanhasConteudo() {
  const [campanhas, setCampanhas] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");
  const [inscritos, setInscritos] = useState([]);
  const [modalAberto, setModalAberto] = useState(false);
  const [campanhaSelecionada, setCampanhaSelecionada] = useState(null);
  const [paginaAtual, setPaginaAtual] = useState(1);

  const itensPorPagina = 10;

  const carregarCampanhas = async () => {
    try {
      const res = await axios.get("/campanhas");
      setCampanhas(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Erro ao carregar campanhas:", err);
      setErro("Erro ao carregar campanhas.");
    }
  };

  useEffect(() => {
    carregarCampanhas();
  }, []);

  useEffect(() => {
    setPaginaAtual(1);
  }, [filtro]);

  const excluirCampanha = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta campanha?")) return;

    try {
      await axios.delete(`/campanhas/${id}`);
      setMensagem("Campanha excluída com sucesso!");
      carregarCampanhas();
    } catch (err) {
      console.error("Erro ao excluir campanha:", err);
      setErro("Erro ao excluir campanha.");
    }
  };

  const alternarStatus = async (id, statusAtual) => {
    try {
      await axios.put(`/campanhas/${id}/status`, { ativa: !statusAtual });
      carregarCampanhas();
    } catch (err) {
      console.error("Erro ao atualizar status da campanha:", err);
      setErro("Erro ao atualizar status.");
    }
  };

  const abrirModalInscritos = async (campanha) => {
    try {
      const res = await axios.get(`/campanhas/${campanha.id}/inscritos`);
      setInscritos(Array.isArray(res.data) ? res.data : []);
      setCampanhaSelecionada(campanha);
      setModalAberto(true);
    } catch (err) {
      console.error("Erro ao buscar inscritos:", err);
      setErro("Erro ao buscar inscritos.");
    }
  };

  const campanhasFiltradas = useMemo(() => {
    return campanhas.filter((c) =>
      (c.titulo || "").toLowerCase().includes(filtro.toLowerCase())
    );
  }, [campanhas, filtro]);

  const totalPaginas = Math.max(
    1,
    Math.ceil(campanhasFiltradas.length / itensPorPagina)
  );

  const campanhasPagina = campanhasFiltradas.slice(
    (paginaAtual - 1) * itensPorPagina,
    paginaAtual * itensPorPagina
  );

  const resumo = useMemo(() => {
    const total = campanhas.length;
    const ativas = campanhas.filter((c) => c.ativa).length;
    const inativas = total - ativas;

    return { total, ativas, inativas };
  }, [campanhas]);

  const formatarNumero = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", {
      maximumFractionDigits: 0,
    });

  const formatarData = (data) => {
    if (!data) return "—";

    try {
      return new Date(data).toLocaleDateString("pt-BR");
    } catch {
      return "—";
    }
  };

  return (
    <div className="campanhasAdmin-page">
      <div className="campanhasAdmin-topbar">
        <div>
          <span className="campanhasAdmin-kicker">Campanhas promocionais</span>
          <h2>Painel de Campanhas</h2>
          <p>
            Gerencie campanhas, status, período de exibição e acompanhe os
            inscritos vinculados a cada ação.
          </p>
        </div>

        <Link to="/campanhas/nova" className="campanhasAdmin-btn primary">
          <Plus size={16} />
          Nova Campanha
        </Link>
      </div>

      {mensagem && <div className="campanhasAdmin-alert success">{mensagem}</div>}
      {erro && <div className="campanhasAdmin-alert danger">{erro}</div>}

      <div className="campanhasAdmin-cards">
        <div className="campanhasAdmin-card">
          <span>Total de campanhas</span>
          <strong>{formatarNumero(resumo.total)}</strong>
        </div>

        <div className="campanhasAdmin-card success">
          <span>Ativas</span>
          <strong>{formatarNumero(resumo.ativas)}</strong>
        </div>

        <div className="campanhasAdmin-card warning">
          <span>Inativas</span>
          <strong>{formatarNumero(resumo.inativas)}</strong>
        </div>
      </div>

      <div className="campanhasAdmin-filtrosCard">
        <div className="campanhasAdmin-searchInput">
          <Search size={15} />
          <input
            type="text"
            placeholder="Buscar por título..."
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
        </div>

        <span>{formatarNumero(campanhasFiltradas.length)} resultado(s)</span>
      </div>

      <div className="campanhasAdmin-tableCard">
        <div className="campanhasAdmin-tableHeader">
          <div>
            <h3>Campanhas cadastradas</h3>
            <span>Campanhas podem ser editadas, inativadas ou removidas.</span>
          </div>

          <ImagePlus size={18} />
        </div>

        <div className="campanhasAdmin-tableWrapper">
          <table className="campanhasAdmin-table">
            <thead>
              <tr>
                <th>Campanha</th>
                <th>Período</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              {campanhasPagina.length === 0 ? (
                <tr>
                  <td colSpan="4" className="campanhasAdmin-empty">
                    Nenhuma campanha encontrada.
                  </td>
                </tr>
              ) : (
                campanhasPagina.map((campanha) => (
                  <tr key={campanha.id}>
                    <td>
                      <div className="campanhasAdmin-campanhaCell">
                        {campanha.imagem ? (
                          <img
                            src={getImagemUrl(campanha.imagem)}
                            alt={campanha.titulo}
                            className="campanhasAdmin-miniatura"
                          />
                        ) : (
                          <div className="campanhasAdmin-miniatura vazio">
                            <ImagePlus size={18} />
                          </div>
                        )}

                        <div className="campanhasAdmin-cellMain">
                          <strong>{campanha.titulo}</strong>
                          <span>ID #{campanha.id}</span>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="campanhasAdmin-cellMain">
                        <strong>
                          {formatarData(campanha.data_inicio)} até{" "}
                          {formatarData(campanha.data_fim)}
                        </strong>
                      </div>
                    </td>

                    <td>
                      <span
                        className={`campanhasAdmin-status ${
                          campanha.ativa ? "ativa" : "inativa"
                        }`}
                      >
                        {campanha.ativa ? "Ativa" : "Inativa"}
                      </span>
                    </td>

                    <td>
                      <div className="campanhasAdmin-actions">
                        <Link
                          to={`/campanhas/editar/${campanha.id}`}
                          className="campanhasAdmin-actionBtn edit"
                        >
                          <Edit size={14} />
                          Editar
                        </Link>

                        <button
                          type="button"
                          className="campanhasAdmin-actionBtn view"
                          onClick={() => abrirModalInscritos(campanha)}
                        >
                          <Eye size={14} />
                          Inscritos
                        </button>

                        <button
                          type="button"
                          className="campanhasAdmin-actionBtn status"
                          onClick={() =>
                            alternarStatus(campanha.id, campanha.ativa)
                          }
                        >
                          <Power size={14} />
                          {campanha.ativa ? "Inativar" : "Reativar"}
                        </button>

                        <button
                          type="button"
                          className="campanhasAdmin-actionBtn delete"
                          onClick={() => excluirCampanha(campanha.id)}
                        >
                          <Trash2 size={14} />
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPaginas > 1 && (
          <div className="campanhasAdmin-paginacao">
            <button
              type="button"
              onClick={() => setPaginaAtual((prev) => Math.max(prev - 1, 1))}
              disabled={paginaAtual === 1}
            >
              Anterior
            </button>

            <span>
              Página {paginaAtual} de {totalPaginas}
            </span>

            <button
              type="button"
              onClick={() =>
                setPaginaAtual((prev) => Math.min(prev + 1, totalPaginas))
              }
              disabled={paginaAtual === totalPaginas}
            >
              Próxima
            </button>
          </div>
        )}
      </div>

      {modalAberto && (
        <div
          className="campanhasAdmin-modalOverlay"
          onClick={() => setModalAberto(false)}
        >
          <div
            className="campanhasAdmin-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="campanhasAdmin-modalHeader">
              <div>
                <h3>Inscritos da campanha</h3>
                <span>{campanhaSelecionada?.titulo}</span>
              </div>

              <button
                type="button"
                className="campanhasAdmin-modalClose"
                onClick={() => setModalAberto(false)}
              >
                <X size={18} />
              </button>
            </div>

            <div className="campanhasAdmin-inscritosLista">
              {inscritos.length === 0 ? (
                <div className="campanhasAdmin-inscritoEmpty">
                  Nenhum inscrito nesta campanha.
                </div>
              ) : (
                inscritos.map((i) => (
                  <div key={i.id} className="campanhasAdmin-inscritoItem">
                    <div className="campanhasAdmin-inscritoIcon">
                      <Users size={16} />
                    </div>

                    <div>
                      <strong>{i.nome}</strong>
                      <span>{i.email}</span>
                      <small>
                        {[i.cidade, i.estado].filter(Boolean).join(" / ") || "—"}
                      </small>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PainelCampanhas() {
  return (
    <ComProtecao rota="campanhas" tipo="pode_ler">
      <PainelCampanhasConteudo />
    </ComProtecao>
  );
}